import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';
import { FleetGeneralFigureService } from 'src/app/_services/fleet-general-figure.service';
import { VehicleInfoService } from 'src/app/_services/vehicle-info.service';
import { VehicleCurrentInfo } from '../../../models/vehicle';
import { RbUiComponentsModule } from '@inst-iot/bosch-angular-ui-components'; 
import { KmToMilesPipe } from '../../../pipes/km-to-miles.pipe';
import { TransformXAxisPipe } from 'src/app/_shared/pipes/transform-xaxis.pipe';

@Component({
  selector: 'app-vehicle-mileage-chart-ef',
  standalone: true,
  imports: [CommonModule,RbUiComponentsModule],
  templateUrl: './vehicle-mileage-chart-ef.component.html',
  styleUrls: ['./vehicle-mileage-chart-ef.component.scss']
})
export class VehicleMileageChartEfComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() fleetId: string[] = [];
  @Input() selectedVehicles: any[] = [];
  @Input() companyData: any;
  @Input() isMetric: boolean | undefined;
  currentVehicleData: any;
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  vehiclesInfo!: { [key: string]: VehicleCurrentInfo; };
  chartRange!: { range: [number, number] };
  private readonly kmToMilesPipe = new KmToMilesPipe();

  constructor(
    private readonly fleetGeneralFigureService: FleetGeneralFigureService,
    private readonly vehicleInfoService: VehicleInfoService,
  ) { }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoading = true;
    this.errorMessage = false;
    this.fleetGeneralFigureService.getMileageFleet(this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe(
      {
        next: (datapoints) => {
          const fleetData = this.plotVehicleMileage(datapoints);
          this.fetchVehicleInfoAndUpdateChart(fleetData);
        },
        error: (error) => {
          console.error('Error fetching Vehicle Mileage Fleet data:', error);
          this.dataLoading = false;
          this.errorMessage = true;
        }
      }
    );
  }

  fetchVehicleInfoAndUpdateChart(fleetData: any[]): void {
    const deviceSerialNumber: string[] = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);
    const vehicleNameMap: { [key: string]: string } = {};

    this.selectedVehicles.forEach(vehicle => {
      vehicleNameMap[vehicle.batteryIdentifier] = vehicle.name;
    });

    this.vehicleInfoService.getCurrentVehicleInfo(deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (data) => {
        this.vehiclesInfo = data;

        const mileageLines = deviceSerialNumber.map((id, index) => {
          const mileage = this.getConvertedMileageValue(this.vehiclesInfo[id].mileage);
          const vehicleName = vehicleNameMap[id];

          const maxPercentAmount = Math.max(...fleetData.flatMap(plot => plot.y));

          return {
            x: [mileage, mileage],
            y: [0, maxPercentAmount],
            type: 'scatter',
            mode: 'lines',
            name: vehicleName,
            line: {
              color: this.getVehicleColor(index),
              width: 8
            }
          };
        });

        const combinedPlotData = [...fleetData, ...mileageLines];
        this.updateChart(combinedPlotData);
        this.dataLoading = false;
      },
      error: (error) => {
        console.error('Error fetching Current Vehicle Info data:', error);
        this.dataLoading = false;
        this.errorMessage = true;
      }
    });
  }

  getVehicleColor(index: number): string {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    return colors[index % 3];
  }

  plotVehicleMileage(datapoints: any): any[] {
    const transformXAxisPipe = new TransformXAxisPipe();
    const data = Object.keys(datapoints).flatMap((fleetId: string) => {
      const plotData = datapoints[fleetId];

      const distance = plotData.map((dataObject: any) => dataObject.binMileage);
      const percentAmount = plotData.map((dataObject: any) => dataObject.percVeh);
      const convertedDistance = this.getConvertedMileageArray(distance);
      this.chartRange = transformXAxisPipe.transform(convertedDistance, percentAmount, 5000, 1000000);

      return {
        x: convertedDistance,
        y: percentAmount,
        type: 'bar',
        name: `Fleet`,
        marker: {
          color: '#8A9097'
        }
      };
    });
    return data;
  }

  updateChart(data: any[]): void {
    const layout = {
      title: 'Vehicle Mileage',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      barmode: 'group',
      xaxis: {
        title: `Mileage [${this.getMileageUnit()}]`,
        range: this.chartRange?.range
      },
      yaxis: {
        title: 'Percentage of Fleet Vehicles [%]'
      },
      legend: {
        orientation: 'h',
        y: -0.3,
        xanchor: 'center',
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
      }
    };

    createPlotlyChart('vehicle-mileage-chart', data, layout);
  }

  getConvertedMileageArray(arr: number[]): number[] {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? arr : arr.map(val => this.kmToMilesPipe.transform(val));
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? arr.map(val => this.kmToMilesPipe.transform(val))
        : arr;
    }
  }

  getConvertedMileageValue(val: number): number {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? val : this.kmToMilesPipe.transform(val);
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? this.kmToMilesPipe.transform(val)
        : val;
    }
  }

  getMileageUnit(): string {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? 'km' : 'mi';
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? 'mi'
        : 'km';
    }
  }
}
